import { Extension, InputRule } from "@tiptap/core"
import { SuggestionPluginKey } from "@tiptap/suggestion"
import { EditorState, TextSelection, Transaction } from "prosemirror-state"
import { Node as ProseMirrorNode } from "prosemirror-model"

import {
  INDENT_UNIT,
  formatQueryIndentation,
  indentWidthForNextLine,
  lineIndentWidth,
  outdentWidth,
} from "./queryIndentation"

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    queryIndent: {
      /** Re-indent the whole script from its FOR/IF block nesting. */
      formatQueryScript: () => ReturnType
      /** Shift every selected line one level right (1) or left (-1). */
      shiftQueryIndent: (direction: 1 | -1) => ReturnType
      /** Split the line, carrying the indent the next line should have. */
      newLineWithIndent: () => ReturnType
    }
  }
}

interface LineInfo {
  /** Position of the line's first character. */
  start: number
  /** Position just past the line's last character. */
  end: number
  text: string
}

/**
 * The console's document is one paragraph per line, so a line is just a
 * top-level child of the doc.
 */
const readLines = (doc: ProseMirrorNode): LineInfo[] => {
  const lines: LineInfo[] = []
  doc.forEach((node, offset) => {
    const start = offset + 1
    lines.push({
      start,
      end: start + node.content.size,
      text: node.textContent,
    })
  })
  return lines
}

// Enter and Tab both mean something to the slash-command popup while it is
// open; let it answer first.
const suggestionIsOpen = (state: EditorState): boolean => {
  const suggestionState = SuggestionPluginKey.getState(state) as
    | { active?: boolean }
    | undefined
  return Boolean(suggestionState?.active)
}

const shiftLines = (
  state: EditorState,
  tr: Transaction,
  direction: 1 | -1
): void => {
  const { from, to } = state.selection
  const targets = readLines(state.doc).filter(
    (line) => line.end >= from && line.start <= to
  )
  // Back to front so the untouched earlier offsets stay valid.
  for (let i = targets.length - 1; i >= 0; i--) {
    const line = targets[i]
    if (direction === 1) {
      tr.insertText(INDENT_UNIT, line.start)
    } else {
      const remove = outdentWidth(line.text)
      if (remove > 0) tr.delete(line.start, line.start + remove)
    }
  }
}

/**
 * Loop-aware indentation for the Query Console editor.
 *
 * - Enter inherits the current line's indent, one level deeper after a
 *   block opener (FOR/IF/IFDEF/IFNDEF, and after ELSE).
 * - Tab / Shift+Tab indent and outdent the selected lines.
 * - Typing ENDFOR/ENDIF/ELSE as the first word of an indented line outdents
 *   that line once. It is an input rule, so it only ever reacts to typing —
 *   a Tab afterwards is not fought.
 * - Shift+Alt+F (or the toolbar's Format button) re-indents the whole script
 *   in a single, undoable transaction.
 *
 * Indentation is safe to send to the engine: its tokenizer ignores
 * intra-line whitespace.
 */
export const QueryIndent = Extension.create({
  name: "queryIndent",

  // Above the core keymap so Enter/Tab reach this extension first; the
  // slash-command popup is deferred to explicitly instead.
  priority: 1000,

  addCommands() {
    return {
      formatQueryScript:
        () =>
        ({ editor, state, tr, dispatch }) => {
          // Reachable from the toolbar button, which the read-only
          // structured-record view must not be able to rewrite.
          if (!editor.isEditable) return false
          const lines = readLines(state.doc)
          const texts = lines.map((line) => line.text)
          const formatted = formatQueryIndentation(texts)
          if (formatted.every((text, index) => text === texts[index])) {
            return false
          }
          if (!dispatch) return true

          const head = state.selection.head
          const cursorLine = lines.findIndex(
            (line) => head >= line.start && head <= line.end
          )
          const cursorColumn =
            cursorLine === -1 ? 0 : head - lines[cursorLine].start

          for (let i = lines.length - 1; i >= 0; i--) {
            if (formatted[i] === texts[i]) continue
            const { start, end } = lines[i]
            if (formatted[i]) tr.insertText(formatted[i], start, end)
            else tr.delete(start, end)
          }

          if (cursorLine !== -1) {
            // Hold the caret on the same character by moving it with its
            // line's indentation. Each paragraph occupies its text length
            // plus its two boundary tokens.
            let lineStart = 1
            for (let i = 0; i < cursorLine; i++) {
              lineStart += formatted[i].length + 2
            }
            const shift =
              lineIndentWidth(formatted[cursorLine]) -
              lineIndentWidth(texts[cursorLine])
            const column = Math.max(
              0,
              Math.min(formatted[cursorLine].length, cursorColumn + shift)
            )
            tr.setSelection(TextSelection.create(tr.doc, lineStart + column))
          }
          return true
        },

      shiftQueryIndent:
        (direction: 1 | -1) =>
        ({ state, tr, dispatch }) => {
          if (suggestionIsOpen(state)) return false
          if (dispatch) shiftLines(state, tr, direction)
          return true
        },

      newLineWithIndent:
        () =>
        ({ state, tr, dispatch }) => {
          if (suggestionIsOpen(state)) return false
          const { selection } = state
          if (!selection.empty || !selection.$from.parent.isTextblock) {
            return false
          }
          const indent = " ".repeat(
            indentWidthForNextLine(selection.$from.parent.textContent)
          )
          if (dispatch) {
            tr.split(selection.from)
            const lineStart = tr.mapping.map(selection.from, 1)
            if (indent) tr.insertText(indent, lineStart)
            tr.setSelection(
              TextSelection.create(tr.doc, lineStart + indent.length)
            )
            tr.scrollIntoView()
          }
          return true
        },
    }
  },

  addKeyboardShortcuts() {
    return {
      Enter: () => this.editor.commands.newLineWithIndent(),
      Tab: () => this.editor.commands.shiftQueryIndent(1),
      "Shift-Tab": () => this.editor.commands.shiftQueryIndent(-1),
      "Shift-Alt-f": () => this.editor.commands.formatQueryScript(),
    }
  },

  addInputRules() {
    return [
      new InputRule({
        find: /^[ \t]+(?:ENDFOR|ENDIF|ELSE)$/,
        handler: ({ state, range, match }) => {
          const line = match[0]
          const remove = outdentWidth(line)
          // Nothing to give back — let the keystroke land normally.
          if (remove === 0) return null
          // `range` covers the line as it stands; the triggering character
          // is only in `match`, so rewrite the whole run to keep it.
          state.tr.insertText(line.slice(remove), range.from, range.to)
        },
      }),
    ]
  },
})

export default QueryIndent
